import BaseAPI from '@/lib/ScormApi/BaseScormAPI'

import {
  SCORM_FALSE,
  SCORM_TRUE,
  STATE_INITIALIZED,
  LOG_LEVEL_INFO,
  STATE_TERMINATED,
  LOG_LEVEL_WARNING
} from './contants'

import CMI_ObjectivesObject from '@/lib/ScormApi/Scorm/CMI_ObjectivesObject'
import CMI_InteractionsCorrectResponsesObject from '@/lib/ScormApi/Scorm/CMI_InteractionsCorrectResponsesObject'
import CMI_InteractionsObjectivesObject from '@/lib/ScormApi/Scorm/CMI_InteractionsObjectivesObject'
import CMI_InteractionsObject from '@/lib/ScormApi/Scorm/CMI_InteractionsObject'
import CMI from '@/lib/ScormApi/Scorm/CMI'
import CMIInterface from "@/lib/ScormApi/Scorm/interfaces/CMIInterface"

export default class ScormAPI extends BaseAPI {
  public cmi: CMIInterface
  constructor () {
    super()
    this.cmi = CMI(this)
  }

  /**
   * @returns {string} bool
   */
  public LMSInitialize () {
    let returnValue = SCORM_FALSE

    if (this.isInitialized()) {
      this.throwSCORMError(101, 'LMS was already initialized!')
    } else if (this.isTerminated()) {
      this.throwSCORMError(101, 'LMS is already finished!')
    } else {
      this.currentState = STATE_INITIALIZED
      returnValue = SCORM_TRUE
      this.processListeners('LMSInitialize')
    }

    this.apiLog('LMSInitialize', null, 'returned: ' + returnValue, LOG_LEVEL_INFO)
    this.clearSCORMError(returnValue)

    return returnValue
  }

  /**
   * @returns {string} bool
   */
  public LMSFinish () {
    let returnValue = SCORM_FALSE

    if (this.checkState()) {
      this.currentState = STATE_TERMINATED
      returnValue = SCORM_TRUE
      this.processListeners('LMSFinish')
    }

    this.apiLog('LMSFinish', null, 'returned: ' + returnValue, LOG_LEVEL_INFO)
    this.clearSCORMError(returnValue)

    return returnValue
  }

  /**
   * @param CMIElement
   * @returns {string}
   */
  public LMSGetValue (CMIElement: string) {
    let returnValue = ''

    if (this.checkState()) {
      returnValue = this.getCMIValue(CMIElement)
      this.processListeners('LMSGetValue', CMIElement, returnValue)
    }

    this.apiLog('LMSGetValue', CMIElement, ': returned: ' + returnValue, LOG_LEVEL_INFO)
    this.clearSCORMError(returnValue)

    return returnValue
  }

  /**
   * @param CMIElement
   * @param value
   * @returns {string}
   */
  public LMSSetValue (CMIElement: string, value: string) {
    let returnValue = ''

    if (this.checkState()) {
      returnValue = this.setCMIValue(CMIElement, value)
      this.processListeners('LMSSetValue', CMIElement, value)
    }

    this.apiLog('LMSSetValue', CMIElement, ': ' + value + ': returned: ' + returnValue, LOG_LEVEL_INFO)
    this.clearSCORMError(returnValue)

    return returnValue
  }

  /**
   * Orders LMS to store all content parameters
   *
   * @returns {string} bool
   */
  public LMSCommit () {
    let returnValue = SCORM_FALSE

    if (this.checkState()) {
      returnValue = SCORM_TRUE
      this.processListeners('LMSCommit')
    }

    this.apiLog('LMSCommit', null, 'returned: ' + returnValue, LOG_LEVEL_INFO)
    this.clearSCORMError(returnValue)

    return returnValue
  }

  /**
   * Returns last error code
   *
   * @returns {string}
   */
  public LMSGetLastError () {
    const returnValue = this.lastErrorCode

    this.processListeners('LMSGetLastError')

    this.apiLog('LMSGetLastError', null, 'returned: ' + returnValue, LOG_LEVEL_INFO)

    return returnValue
  }

  /**
   * Returns the errorNumber error description
   *
   * @param CMIErrorCode
   * @returns {string}
   */
  public LMSGetErrorString (CMIErrorCode: string) {
    let returnValue = ''

    if (CMIErrorCode !== null && CMIErrorCode !== '') {
      returnValue = this.getLmsErrorMessageDetails(+CMIErrorCode)
      this.processListeners('LMSGetErrorString')
    }

    this.apiLog('LMSGetErrorString', null, 'returned: ' + returnValue, LOG_LEVEL_INFO)

    return returnValue
  }

  /**
   * Returns a comprehensive description of the errorNumber error.
   *
   * @param CMIErrorCode
   * @returns {string}
   */
  public LMSGetDiagnostic (CMIErrorCode: string) {
    let returnValue = ''

    if (CMIErrorCode !== null && CMIErrorCode !== '') {
      returnValue = this.getLmsErrorMessageDetails(+CMIErrorCode, true)
      this.processListeners('LMSGetDiagnostic')
    }

    this.apiLog('LMSGetDiagnostic', null, 'returned: ' + returnValue, LOG_LEVEL_INFO)

    return returnValue
  }

  /**
   * Checks the LMS state and ensures it has been initialized
   */
  public checkState () {
    if (!this.isInitialized()) {
      this.throwSCORMError(301)
      return false
    }

    return true
  }

  /**
   * Loads CMI data from a JSON object.
   */
  public loadFromJSON (json: any, CMIElement: string) {
    if (!this.isNotInitialized()) {
      console.error('loadFromJSON can only be called before the call to LMSInitialize.')
      return
    }

    CMIElement = CMIElement || 'cmi'

    for (const key in json) {
      if (json.hasOwnProperty(key) && json[key]) {
        const currentCMIElement = CMIElement + '.' + key
        const value = json[key]

        if (value.childArray) {
          for (let i = 0; i < value.childArray.length; i++) {
            this.loadFromJSON(value.childArray[i], currentCMIElement + '.' + i)
          }
        } else if (value.constructor === Object) {
          this.loadFromJSON(value, currentCMIElement)
        } else {
          this.setCMIValue(currentCMIElement, value)
        }
      }
    }
  }

  /**
   * Returns the message that corresponds to errrorNumber.
   */
  protected getLmsErrorMessageDetails (errorNumber: number, detail = false) {
    let basicMessage = ''
    let detailMessage = ''

    // Set error number to string since inconsistent from modules if string or number
    const errorCode = String(errorNumber)

    switch (errorCode) {
      case '101':
        basicMessage = 'General Exception'
        detailMessage = 'No specific error code exists to describe the error. Use LMSGetDiagnostic for more information'
        break

      case '201':
        basicMessage = 'Invalid argument error'
        detailMessage = 'Indicates that an argument represents an invalid data model element or is otherwise incorrect.'
        break

      case '202':
        basicMessage = 'Element cannot have children'
        detailMessage = 'Indicates that LMSGetValue was called with a data model element name that ends in "_children" for a data model element that does not support the "_children" suffix.'
        break

      case '203':
        basicMessage = 'Element not an array - cannot have count'
        detailMessage = 'Indicates that LMSGetValue was called with a data model element name that ends in "_count" for a data model element that does not support the "_count" suffix.'
        break

      case '301':
        basicMessage = 'Not initialized'
        detailMessage = 'Indicates that an API call was made before the call to LMSInitialize.'
        break

      case '401':
        basicMessage = 'Not implemented error'
        detailMessage = 'The data model element indicated in a call to LMSGetValue or LMSSetValue is valid, but was not implemented by this LMS. SCORM 1.2 defines a set of data model elements as being optional for an LMS to implement.'
        break

      case '402':
        basicMessage = 'Invalid set value, element is a keyword'
        detailMessage = 'Indicates that LMSSetValue was called on a data model element that represents a keyword (elements that end in "_children" and "_count").'
        break

      case '403':
        basicMessage = 'Element is read only'
        detailMessage = 'LMSSetValue was called with a data model element that can only be read.'
        break

      case '404':
        basicMessage = 'Element is write only'
        detailMessage = 'LMSGetValue was called on a data model element that can only be written to.'
        break

      case '405':
        basicMessage = 'Incorrect Data Type'
        detailMessage = 'LMSSetValue was called with a value that is not consistent with the data format of the supplied data model element.'
        break

      default:
        basicMessage = 'No Error'
        detailMessage = 'No Error'
        break
    }

    return detail ? detailMessage : basicMessage
  }

  /**
   * Sets a value on the CMI Object
   *
   * @param CMIElement
   * @param value
   * @returns {string}
   */
  private setCMIValue (CMIElement: string, value: string) {
    if (!CMIElement || CMIElement === '') {
      return SCORM_FALSE
    }

    const structure = CMIElement.split('.')
    let refObject: any = this
    let found = SCORM_FALSE

    for (let i = 0; i < structure.length; i++) {
      if (i === structure.length - 1) {
        if (!refObject.hasOwnProperty(structure[i])) {
          this.throwSCORMError(101, 'setCMIValue did not find an element for: ' + CMIElement)
        } else {
          refObject[structure[i]] = value
          found = SCORM_TRUE
        }
      } else {
        refObject = refObject[structure[i]]
        if (!refObject) {
          this.throwSCORMError(101, 'setCMIValue did not find an element for: ' + CMIElement)
          break
        }

        if (refObject.hasOwnProperty('childArray')) {
          const index = parseInt(structure[i + 1], 10)

          // SCO is trying to set an item on an array
          if (!isNaN(index)) {
            const item = refObject.childArray[index]

            if (item) {
              refObject = item
            } else {
              let newChild

              if (CMIElement.indexOf('cmi.objectives') > -1) {
                newChild = CMI_ObjectivesObject(this)
              } else if (CMIElement.indexOf('.correct_responses') > -1) {
                newChild = CMI_InteractionsCorrectResponsesObject(this)
              } else if (CMIElement.indexOf('.objectives') > -1) {
                newChild = CMI_InteractionsObjectivesObject(this)
              } else if (CMIElement.indexOf('cmi.interactions') > -1) {
                newChild = CMI_InteractionsObject(this)
              }

              if (!newChild) {
                this.throwSCORMError(101, 'Cannot create new sub entity: ' + CMIElement)
              } else {
                refObject.childArray.push(newChild)
                refObject = newChild
              }
            }

            // Have to update i value to skip the array position
            i++
          }
        }
      }
    }

    if (found === SCORM_FALSE) {
      this.apiLog('LMSSetValue', null, 'There was an error setting the value for: ' + CMIElement + ', value of: ' + value, LOG_LEVEL_WARNING)
    }

    return found
  }

  /**
   * Gets a value from the CMI Object
   *
   * @param CMIElement
   * @returns {*}
   */
  private getCMIValue (CMIElement: string) {
    if (!CMIElement || CMIElement === '') {
      return ''
    }

    const structure = CMIElement.split('.')
    let refObject: any = this
    let lastProperty = null

    for (let i = 0; i < structure.length; i++) {
      lastProperty = structure[i]

      if (i === structure.length - 1) {
        if (!refObject.hasOwnProperty(structure[i])) {
          this.throwSCORMError(101, 'getCMIValue did not find a value for: ' + CMIElement)
        }
      }

      refObject = refObject[structure[i]]
    }

    if (refObject === null || refObject === undefined) {
      if (lastProperty === '_children') {
        this.throwSCORMError(202)
      } else if (lastProperty === '_count') {
        this.throwSCORMError(203)
      }
      return ''
    } else {
      return refObject
    }
  }
}
